import { Badge } from '@/components/ui/badge'
import { Database, Shield, Key } from 'lucide-react'

export interface SubjectAttributeItemData {
  readonly key: string
  readonly value: unknown
  readonly dataType: string
  readonly source: string
  readonly confidence: number
  readonly validFrom: string
}

export interface SubjectAttributesCardData {
  readonly message: string
  readonly subjectId: string
  readonly attributes: readonly SubjectAttributeItemData[]
}

const SOURCE_STYLES: Record<string, string> = {
  MANUAL: 'text-blue-600 dark:text-blue-400',
  LDAP: 'text-green-600 dark:text-green-400',
  AZURE_AD: 'text-purple-600 dark:text-purple-400',
  OKTA: 'text-orange-600 dark:text-orange-400',
  CSV_IMPORT: 'text-gray-600 dark:text-gray-400',
  JSON_IMPORT: 'text-gray-600 dark:text-gray-400',
  API_SYNC: 'text-cyan-600 dark:text-cyan-400',
}

export function SubjectAttributesCard({ data }: { data: SubjectAttributesCardData }) {
  if (data.attributes.length === 0) {
    return (
      <div className="my-2 rounded-lg border border-border dark:border-border/60 bg-card shadow-sm px-3.5 py-3">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Key className="size-4" />
          <span>{data.message}</span>
        </div>
      </div>
    )
  }

  return (
    <div className="my-2 rounded-lg border border-border dark:border-border/60 bg-card shadow-sm overflow-hidden">
      <div className="flex items-center gap-2 px-3.5 py-2.5 border-b border-border/60">
        <Shield className="size-3.5 text-cyan-600 dark:text-cyan-400" />
        <span className="text-xs font-medium text-foreground">{data.message}</span>
      </div>
      <div className="divide-y divide-border/50">
        {data.attributes.map((attr) => (
          <AttributeRow key={attr.key} attribute={attr} />
        ))}
      </div>
    </div>
  )
}

function AttributeRow({ attribute }: { attribute: SubjectAttributeItemData }) {
  const sourceStyle = SOURCE_STYLES[attribute.source] ?? SOURCE_STYLES.MANUAL
  const formattedValue = formatValue(attribute.value)

  return (
    <div className="flex items-center gap-3 px-3.5 py-2">
      <div className="flex items-center justify-center size-5 rounded shrink-0 bg-muted">
        <Database className="size-2.5 text-muted-foreground" strokeWidth={1.5} />
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono font-medium text-foreground">{attribute.key}</span>
          <span className="text-[10px] text-muted-foreground font-mono">{attribute.dataType}</span>
        </div>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span className="text-xs font-medium text-foreground max-w-40 truncate">{formattedValue}</span>
        <Badge variant="outline" className="text-[9px] px-1 py-0 font-mono">
          <span className={sourceStyle}>{attribute.source}</span>
        </Badge>
        {attribute.confidence < 1.0 && (
          <span className="text-[9px] text-muted-foreground">{Math.round(attribute.confidence * 100)}%</span>
        )}
      </div>
    </div>
  )
}

function formatValue(value: unknown): string {
  if (value === null || value === undefined) return 'null'
  if (typeof value === 'string') return value
  if (typeof value === 'boolean') return value ? 'true' : 'false'
  if (typeof value === 'number') return String(value)
  if (Array.isArray(value)) return value.map(String).join(', ')
  return JSON.stringify(value)
}
